/**
 * Heightmap and world import/export.
 *
 * Two formats, because they answer different questions:
 *
 *  - **PNG** (16-bit) is the interchange format: it opens in QGIS, Blender, a
 *    photo editor, anywhere. 16 bits over the terrain's height range is ~3 cm of
 *    vertical resolution, which is finer than the simulation's own noise floor.
 *  - **`.f32` / JSON** are the archival formats: the full packed state, every
 *    field at full float precision, so a session can be resumed exactly.
 *
 * PNG encoding is done through a canvas (the browser's own encoder), and the
 * 16-bit data is stored in the red and green channels of an RGBA image — the
 * standard trick, since `canvas` cannot write 16-bit greyscale directly.
 */

import { F, STATE_STRIDE } from '../simulation/constants.ts';
import type { SimParams } from '../config.ts';

export interface HeightmapPayload {
  /** Raw 16-bit samples, row-major, top row first. */
  data: Uint16Array;
  width: number;
  height: number;
}

export function download(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next task so the click has definitely been handled.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/* -------------------------------------------------------------------------- */
/* Heightmap extraction                                                       */
/* -------------------------------------------------------------------------- */

/** Pack the height field into 16-bit samples over `[0, maxHeight]`. */
export function stateToHeightmap(state: Float32Array, width: number, maxHeight: number): HeightmapPayload {
  const n = width * width;
  const data = new Uint16Array(n);
  const inv = 65535 / Math.max(1e-6, maxHeight);
  for (let k = 0; k < n; k++) {
    const h = state[k * STATE_STRIDE + F.height];
    data[k] = Math.max(0, Math.min(65535, Math.round(h * inv)));
  }
  return { data, width, height: width };
}

/**
 * Rebuild a full packed state from a heightmap.
 *
 * The imported surface becomes both `height` and `bedrock`, with a thin regolith
 * cover: importing a bare height field says nothing about soil or hardness, and
 * assuming a deep soil layer would silently change how the terrain erodes. A
 * fixed modest cover keeps the import honest and lets the user paint more on.
 */
export function heightmapToState(map: HeightmapPayload, maxHeight: number, target: number): Float32Array {
  const out = new Float32Array(target * target * STATE_STRIDE);
  const scale = maxHeight / 65535;
  const soilCover = Math.max(0.05, maxHeight * 0.004);
  for (let j = 0; j < target; j++) {
    const sj = Math.min(map.height - 1, Math.round((j / Math.max(1, target - 1)) * (map.height - 1)));
    for (let i = 0; i < target; i++) {
      const si = Math.min(map.width - 1, Math.round((i / Math.max(1, target - 1)) * (map.width - 1)));
      const h = map.data[sj * map.width + si] * scale;
      const b = j * target + i;
      out[b * STATE_STRIDE + F.height] = h;
      out[b * STATE_STRIDE + F.bedrock] = Math.max(0, h - soilCover);
      out[b * STATE_STRIDE + F.soil] = Math.min(soilCover, h);
      out[b * STATE_STRIDE + F.hardness] = 1.5;
    }
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* PNG                                                                        */
/* -------------------------------------------------------------------------- */

/** Encode a heightmap as an RGBA PNG carrying 16 bits in R (high) and G (low). */
export async function heightmapToPngBlob(map: HeightmapPayload): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = map.width;
  canvas.height = map.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable for PNG encoding');
  const img = ctx.createImageData(map.width, map.height);
  for (let k = 0; k < map.data.length; k++) {
    const v = map.data[k];
    img.data[k * 4] = (v >> 8) & 0xff;
    img.data[k * 4 + 1] = v & 0xff;
    img.data[k * 4 + 2] = 0;
    img.data[k * 4 + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png');
  });
}

/**
 * Decode a PNG into a heightmap.
 *
 * Accepts both the 16-bit-in-RG convention written above and a plain 8-bit
 * greyscale image, because the most likely thing a user imports is a grayscale
 * heightmap exported from somewhere else.
 */
export async function pngBlobToHeightmap(blob: Blob): Promise<HeightmapPayload> {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D canvas context unavailable for PNG decoding');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Detect the 16-bit convention: the blue channel is zero everywhere and the
  // image is not a flat single value.
  let blueNonZero = 0;
  let greenNonZero = 0;
  for (let k = 0; k < img.data.length; k += 4) {
    if (img.data[k + 2] !== 0) blueNonZero++;
    if (img.data[k + 1] !== 0) greenNonZero++;
  }
  const isSixteenBit = blueNonZero === 0 && greenNonZero > 0;

  const n = canvas.width * canvas.height;
  const data = new Uint16Array(n);
  if (isSixteenBit) {
    for (let k = 0; k < n; k++) data[k] = (img.data[k * 4] << 8) | img.data[k * 4 + 1];
  } else {
    for (let k = 0; k < n; k++) data[k] = img.data[k * 4] * 257;
  }
  return { data, width: canvas.width, height: canvas.height };
}

/* -------------------------------------------------------------------------- */
/* Binary state                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `.f32` world file: a small ASCII header then the packed state verbatim.
 *
 * The header carries the grid size and the parameter block, so a saved world is
 * self-describing and can be restored years later without guessing what settings
 * produced it.
 */
export function encodeWorld(state: Float32Array, params: SimParams, width: number): Blob {
  const header = JSON.stringify({
    format: 'mountain-lab/world',
    version: 1,
    width,
    stride: STATE_STRIDE,
    params,
  });
  const headerBytes = new TextEncoder().encode(header);
  const prefix = new ArrayBuffer(8);
  new DataView(prefix).setUint32(0, 0x4d4c4142, true); // "MLAB"
  new DataView(prefix).setUint32(4, headerBytes.length, true);
  return new Blob([prefix, headerBytes, state.buffer as ArrayBuffer], { type: 'application/octet-stream' });
}

export async function decodeWorld(
  blob: Blob,
): Promise<{ state: Float32Array; params: SimParams; width: number }> {
  const buf = await blob.arrayBuffer();
  const view = new DataView(buf);
  if (view.getUint32(0, true) !== 0x4d4c4142) throw new Error('not a Mountain Lab world file');
  const headerLength = view.getUint32(4, true);
  const headerText = new TextDecoder().decode(new Uint8Array(buf, 8, headerLength));
  const header = JSON.parse(headerText) as {
    width: number;
    stride: number;
    params: SimParams;
    format: string;
  };
  if (header.stride !== STATE_STRIDE) {
    throw new Error(`state layout mismatch: file has stride ${header.stride}, expected ${STATE_STRIDE}`);
  }
  const offset = 8 + headerLength;
  const state = new Float32Array(buf, offset, (buf.byteLength - offset) / 4);
  return { state, params: header.params, width: header.width };
}

/** Open a file picker and resolve with the chosen file, or null if cancelled. */
export function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = () => resolve(input.files?.[0] ?? null);
    // A cancelled dialog fires nothing in most browsers, so the promise simply
    // never settles; that is harmless here because nothing awaits cleanup.
    input.click();
  });
}
